import { useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components/native';
import { ScreenNamesEnum } from '@/constants';
import { AppTextView } from '@/components/base/app.text.view';
import { AppTextField } from '@/components/base/app.text.field';
import { Box } from '@/components/base/box';
import { AppButton } from '@/components/base/app.button';
import { useAppRouter } from '@/hooks/use.app.router';
import { AppModal } from '@/components/compose/app.modal';
import { Platform } from 'react-native';
import { useSocket } from '@/hooks/use.socket';
import { useVisitPreview } from '@/apis/visit';
import { useVerifyDiscount } from '@/apis/discount';
import { useGenerateGatewayToken } from '@/apis/gateway';
import { numbersToEnglish, numberWithCommas, openURL } from '../../utils';
import { AppCardView, generateShadow } from '@/components/base/app.card.view';
import { useAppDispatch } from '@/hooks/use.redux';
import { actionShowAlert } from '@/redux';

export default function PaymentModal() {
  const router = useAppRouter();
  const theme = useTheme();
  const { params } = useAppRouter();
  const dispatch = useAppDispatch();
  const cost = (params as any).cost;
  const code = (params as any).code;
  const [discountCode, setDiscountCode] = useState('');
  const { initiateVisit } = useSocket();
  const [editable, setEditable] = useState(false);
  const { data: discountData } = useVerifyDiscount({ query: { code: discountCode } }, { enabled: discountCode.length === 6 });

  const { data: visitPreview, isPending: isCreditsFetching } = useVisitPreview(
    {
      query: { doctorCode: code },
    },
    {
      refetchOnWindowFocus: 'always',
      gcTime: 0,
      staleTime: 0,
    },
  );

  const credit = visitPreview?.balance || 0;

  useEffect(() => {
    const id = setTimeout(() => {
      setEditable(true);
    }, 500);
    return () => clearTimeout(id);
  }, []);

  const payable = useMemo(() => {
    return Math.max(cost - Math.max(Number(discountData?.discountAmount || 0), 0) - credit, 0);
  }, [cost, discountData?.discountAmount, credit]);

  const { refetch, isFetching: isGatewayUrlFetching } = useGenerateGatewayToken(
    {
      query: {
        amount: String(Math.ceil(Math.max(payable, 1000))),
        doctorCode: code,
        os: Platform.OS,
      },
    },
    { enabled: false },
  );

  return (
    <AppModal blur={true} title={'پرداخت هزینه مشاوره'}>
      <Container>
        <CreditContainer variant={'soft-green'} isFirst>
          <AppTextView variant={'medium'} textAlign={'right'} textColor={'blue'} text={'اعتبار شما:'} />
          <AppTextView variant={'medium'} textAlign={'right'} textColor={'blue'} text={`${numberWithCommas(String(credit))} تومان `} />
        </CreditContainer>

        {discountData && (
          <CreditContainer variant={'soft-green'}>
            <AppTextView variant={'medium'} textAlign={'right'} textColor={'blue'} text={'تخفیف:'} />
            <AppTextView variant={'medium'} textAlign={'right'} textColor={'blue'} text={`${numberWithCommas(String(discountData.discountAmount))} تومان `} />
          </CreditContainer>
        )}

        <CreditContainer variant={'soft-green'}>
          <AppTextView variant={'medium'} textAlign={'right'} textColor={'blue'} text={'مبلغ ویزیت:'} />
          <AppTextView variant={'medium'} textAlign={'right'} textColor={'blue'} text={`${numberWithCommas(String(cost))} تومان `} />
        </CreditContainer>

        <DiscountInput
          editable={editable}
          autoFocus={false}
          maxLength={6}
          value={discountCode}
          placeholderTextColor={'#BDBDBD'}
          placeholder="ورود کد تخفیف"
          onChangeText={(text) => setDiscountCode(text)}
          style={[generateShadow({ shadowColor: theme.colors.jadeDark, blurRadius: 10, shadowOpacity: 0.08, shadowOffsetX: 0, shadowOffsetY: 4 })]}
        />

        <Button
          shadow={'green'}
          loading={isCreditsFetching || isGatewayUrlFetching || !visitPreview}
          onPress={() => {
            if (payable === 0) {
              initiateVisit({ doctorCode: numbersToEnglish(code), discountCode: numbersToEnglish(discountCode) });
              router.resetStackTo(ScreenNamesEnum.MainScreen);
            } else {
              refetch()
                .then((res) => {
                  openURL(res.data.url, '_self');
                  //  window.location.href = `${process.env.EXPO_PUBLIC_API_URL}/user/gateway/redirect?url=${res.data.url}`;
                })
                .catch(() => {
                  dispatch(actionShowAlert('خطا در دریافت لینک پرداخت'));
                });
            }
          }}
          title={payable === 0 ? 'ادامه' : `پرداخت ${numberWithCommas(payable)} تومان`}
        />
      </Container>
    </AppModal>
  );
}

const Container = styled(Box)`
  align-items: center;
  width: 100%;
`;

/* Upgraded to AppCardView for shadow support */
const CreditContainer = styled(AppCardView)<{ isFirst?: boolean }>`
  width: ${({ theme }) => theme.wp(76)}px;
  height: ${({ theme }) => theme.hp(6.5)}px;
  min-height: 50px;
  /* Keep the large gap for the first item, tighten it for subsequent items */
  margin-top: ${({ theme, isFirst }) => (isFirst ? theme.hp(4.5) : theme.hp(2.2))}px;
  border-radius: ${({ theme }) => theme.hp(1.2)}px;
  background-color: white; /* Removed greyWhite to let the shadow do the work */
  align-items: center;
  justify-content: space-between;
  flex-direction: row-reverse;
  padding-horizontal: 4%;
`;

const Button = styled(AppButton)`
  margin-top: ${({ theme }) => theme.hp(3)}px; /* Slightly increased margin to balance the shadow */
  width: ${({ theme }) => theme.wp(76)}px;
`;

const DiscountInput = styled(AppTextField).attrs({
  textStyle: {
    textAlign: 'center',
  },
})`
  width: ${({ theme }) => theme.wp(76)}px;
  height: ${({ theme }) => theme.hp(6.5)}px;
  min-height: 50px;
  margin-top: ${({ theme }) => theme.hp(2.2)}px;
  background-color: white; /* Ensure background is solid for the shadow */
  border-radius: ${({ theme }) => theme.hp(1.2)}px;
`;
